/**
 * Shared dependencies
 */
import { CUSTOM_DATA_EVENTS } from '../shared/constants/events';
import type {
	OpenModalPayload,
	OpenModalCallbackEvent,
	CloseModalCallbackEvent,
} from '../shared/types/modal';

/**
 * Internal dependencies
 */
import { BaseEvent } from './BaseEvent';

/**
 * Event handler for modal-specific events.
 * Extends the BaseEvent to provide methods for
 * listening to and dispatching modal-related events.
 */
export class ModalEvent extends BaseEvent {
	/**
	 * Listens for the modal open event.
	 * @param {OpenModalCallbackEvent} callback - Function to handle the event. Receives the full CustomEvent.
	 * @return {() => void} A function to unsubscribe the listener.
	 */
	onOpen(callback: OpenModalCallbackEvent): () => void {
		return this.listen(CUSTOM_DATA_EVENTS.MODAL_OPEN, callback);
	}

	/**
	 * Dispatches the modal open event.
	 * @param {OpenModalPayload} payload - The data to send with the event.
	 */
	open(payload: OpenModalPayload) {
		this.dispatch(CUSTOM_DATA_EVENTS.MODAL_OPEN, payload);
	}

	/**
	 * Listens for the modal close event.
	 * @param {CloseModalCallbackEvent} callback - Function to handle the event. Receives the full CustomEvent.
	 * @return {() => void} A function to unsubscribe the listener.
	 */
	onClose(callback: CloseModalCallbackEvent): () => void {
		return this.listen(CUSTOM_DATA_EVENTS.MODAL_CLOSE, callback);
	}

	/**
	 * Dispatches the modal close event.
	 */
	close() {
		this.dispatch(CUSTOM_DATA_EVENTS.MODAL_CLOSE, {});
	}

	/**
	 * Listens for toggle edit mode event.
	 * @param {() => void} callback - Function to handle the event.
	 * @return {() => void} A function to unsubscribe the listener.
	 */
	onToggleEditMode(callback: () => void): () => void {
		return this.listen(CUSTOM_DATA_EVENTS.MODAL_TOGGLE_EDIT_MODE, callback);
	}

	/**
	 * Dispatches the toggle edit mode event.
	 */
	toggleEditMode() {
		this.dispatch(CUSTOM_DATA_EVENTS.MODAL_TOGGLE_EDIT_MODE, {});
	}
}
